import { yupResolver } from "@hookform/resolvers/yup";
import { SpinningButton } from "Components/ReactstrapSlim";
import { CheckboxControlled, InputFL, PasswordInputFL, ValidationSummary } from "Components/Inputs";
import React, { useEffect, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useApiClient } from "Services/API/useApiClient";
import { AccountClient, isLogonSuccessResponseTwoFactorConfirmationRequired, isLogonSuccessResponseTwoFactorRegistrationRequired, LogonSuccessResponse } from "Services/LegacyAPI/AccountClient";
import * as yup from "yup";
import { SeparatorWithText } from "./SeparatorWithText";
import { SigningEmailProps, useSigningEmailEffect } from "./SigningCommon";
import { SsoButtons } from "./SsoButtons";
import { TermsOfUseModal } from "./TermsOfUseModal";

export interface LoginFormModel {
   email: string,
   password: string,
   rememberMe?: boolean,
}

const LoginFormSchema: yup.SchemaOf<LoginFormModel> = yup.object({
   email: yup.string()
      .required("Email is required")
      .email("Email is not valid")
      .defined(),
   password: yup.string().required("Password is required")
      .min(LiquidSpaceConfig.MinPasswordLength, "Password must be at least ${min} characters long")
      .max(LiquidSpaceConfig.MaxPasswordLength, "Enter no more than ${max} characters")
      .test("no-spaces", "Password shouldn't contain spaces", (value) =>
         !value || !value.includes(" ")),
   rememberMe: yup.boolean().optional(),
}).defined();



export type LoginFormProps = SigningEmailProps & LSStore.SigningLoginProps & {
   isActive: boolean;
   onSwitchToForgotPassword: () => void;
   onSwitchToSignup: () => void;
   onSwtichToChangePassword: (changePasswordOptions: LSWebUI.Models.ForgotPasswordViewModel) => void;
   onLoginSuccess: (logonResult: LogonSuccessResponse, model: LoginFormModel) => boolean;
}

export const LoginForm: React.FC<LoginFormProps> = function ({ isActive, onLoginSuccess, onSwitchToForgotPassword, onSwitchToSignup, onSwtichToChangePassword, message, ...signingEmailProps }) {
   const accountClient = useApiClient(AccountClient);
   // const layoutContext = useContext(LayoutContext);
   // const accountClient = useMemo(() => {
   //    const client = new AccountClient();
   //    client.onError = (message, response) => {
   //       debugger;
   //       layoutContext.showError(message);
   //    };
   //    return client;
   // }, [])

   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [showTermsOfUse, setShowTermsOfUse] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);
   const [termsOfUseAccepted, setTermsOfUseAccepted] = useState<boolean | undefined>(undefined);

   const formMethods = useForm<LoginFormModel>({
      resolver: yupResolver(LoginFormSchema),
      defaultValues: {
         rememberMe: true,
         email: signingEmailProps.email,
      },
      mode: "onTouched"
   });

   useSigningEmailEffect(signingEmailProps, formMethods, "email");

   const onSubmit = async () => {
      if (isLoading)
         return;
      setIsLoading(true);
      setServerErrors([]);
      let finalIsLoading = false;
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();

            const logonResult = await accountClient.logon({
               UserName: formValues.email,
               Password: formValues.password,
               RememberMe: formValues.rememberMe,
               TermsOfUseAccepted: termsOfUseAccepted
            });
            
            if (logonResult.success) {
               if (!isLogonSuccessResponseTwoFactorRegistrationRequired(logonResult)
                  && !isLogonSuccessResponseTwoFactorConfirmationRequired(logonResult)
                  && logonResult.showTermsOfUse) {
                  setShowTermsOfUse(true);
               } else {
                  finalIsLoading = onLoginSuccess(logonResult, formValues);
               }
            } else {
               if ((logonResult.logonStatus == LSCommon.Enums.LogonStatusEnum.ForcedPasswordChange
                  || logonResult.logonStatus == LSCommon.Enums.LogonStatusEnum.PasswordExpired)
                  && logonResult.passwordValid) {
                  onSwtichToChangePassword({
                     UserName: formValues.email,
                     OldPassword: formValues.password,

                     ConfirmPassword: null,
                     ForcePasswordChange: false,
                     NewPassword: null
                  });
               }
               else {
                  setServerErrors([["email", logonResult.error]]);
               }
            }
         }
         else {
            let name: keyof FieldErrors<LoginFormModel>;
            for (name in formMethods.formState.errors) {
               formMethods.setFocus(name);
               break;
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["email", err.error]]);
         }
         console.log(err);
      } finally {
         setIsLoading(finalIsLoading);
      }
   }

   const onForgotPasswordClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onSwitchToForgotPassword();
   }
   const onSignUpClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onSwitchToSignup();
   }

   const submitOnEnterHandler = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
         onSubmit();
      }
   }

   const onTermsOfUseAccept = () => {
      setTermsOfUseAccepted(true);
      setShowTermsOfUse(false);
      onSubmit();
   }

   useEffect(() => {
      if (isActive) {
         setTimeout(function () {
            formMethods.setFocus("email");
         }, 500);
      }
   }, [isActive])

   return (
      <div className="signing-mode-inner">
         <div className="signing-header" aria-label="log in">
            Log in
         </div>
         {message && <div className="signing-caption">{message}</div>}
         <div className="signing-login-form">
            <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
            <InputFL control={formMethods.control} name="email" placeholder="Email" aria-label="email" className="slf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
            <PasswordInputFL control={formMethods.control} name="password" placeholder="Password" aria-label="password" className="slf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
            <div className="slf-forgot-wrapper">
               <CheckboxControlled control={formMethods.control} name="rememberMe" className="slf-checkbox" aria-label="stay signed in" label="Stay signed in" id="rememberMe-login" />
               <a href="#" className="h8" onClick={onForgotPasswordClick}>Forgot password?</a>
            </div>
            <SpinningButton isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="slf-cta">Log in</SpinningButton>
            <div className="signing-mode-switcher">Don’t have an account? <a href="#" onClick={onSignUpClick}>Sign up</a></div>
            <SeparatorWithText text="OR" />
            <SsoButtons size="md" />
         </div>
         <TermsOfUseModal show={showTermsOfUse} onAccept={onTermsOfUseAccept} />
      </div>
   )
}